﻿import { memo, useEffect } from "react";
import { Paper, Skeleton, Typography } from "@mui/material";
import { HubConnectionState } from "@microsoft/signalr";
import { useQuery } from '@tanstack/react-query';

import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { useSignalRContext } from "../../Contexts/SignalRContext";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { ShiftLockState, isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import { LiveIndicatorState } from "../DashboardCard";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { faIndustry } from "@fortawesome/free-solid-svg-icons";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";

type PlantStatusCardProps = {
  isFullScreen?: boolean
  shiftLockState: ShiftLockState | null;
  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
};

export const PlantStatusCard: React.FC<PlantStatusCardProps> = memo(({ isFullScreen, shiftLockState, onLiveIndicatorChange }) => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid && !!selectedShift;
  const { data: plantData = null, isLoading } = useQuery({
    queryKey: ['dashboard', 'plantStatus', selectedDate.toISODate(), selectedShift],
    queryFn: async () => {
      const res = await dashboardsAjax.getPlantStatusCardData(selectedDate, selectedShift);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: queryEnabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const showSkeleton = isLoading || !queryEnabled;
  const isEmpty = !showSkeleton && !plantData?.entries?.length;

  useEffect(() => {
    const connected = connection?.state === HubConnectionState.Connected;
    const _isCurrentShift = isCurrentShift(selectedDate, selectedShift[0] as "D" | "N", shiftLockState);

    onLiveIndicatorChange?.({
      active: connected,
      showLabel: connected && _isCurrentShift,
      tooltipText: _isCurrentShift
        ? "Data displayed for the latest shift"
        : "Data displayed for selected shift",
    });
  }, [connection?.state, onLiveIndicatorChange, selectedDate, selectedShift, shiftLockState]);

  return (
    <Paper className="!shadow-none !border-none !w-full p-3 text-center h-full relative overflow-y-auto !rounded-none !bg-theme-surface-2">
      {showSkeleton && (
        <div className={`flex flex-wrap animate-fade-in justify-center ${isFullScreen ? "gap-x-2 gap-y-4 md:gap-x-8 md:gap-y-12" : "gap-x-1 gap-y-2 md:gap-x-4 md:gap-y-2"}`}> {/* Note: Loading placeholder*/}
          {[...Array(16)].map((_, i) => (
            <div
              key={i}
              className={`animate-pulse rounded-xl flex-1 min-w-[160px] max-w-[280px] shadow-md px-2 py-4 flex flex-col items-center justify-center bg-theme-table-heading ${isFullScreen ? "h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "h-[150px]"}`}
            >
              <Skeleton animation="wave" variant="text" width="60%" height={28} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="50%" height={20} className="bg-theme-table-row-even" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          icon={faIndustry}
          title={isOnline ? "No plant status" : "Not available offline"}
          subtitle={isOnline
            ? "No plant availability has been recorded for the selected date and shift."
            : "Plant availability is calculated by the server, so it cannot be shown while offline. It is available again once you reconnect."}
        />
      )}
      {!isEmpty && (
      <div className={`h-full transition-all ${!showSkeleton ? "opacity-100 fade-up" : "opacity-0"}`}>
        <div className="pb-2">
          <div className={`animate-fade-in h-full flex flex-wrap justify-center ${isFullScreen ? "gap-x-2 gap-y-4 md:gap-x-8 md:gap-y-12" : "gap-x-1 gap-y-2 md:gap-x-4 md:gap-y-2"}`}>
            {plantData?.entries?.map((plant, i) => (
              <div
                key={i}
                className={`
                rounded-xl flex-1 min-w-[160px] max-w-[280px] text-white px-2 py-4 flex flex-col items-center justify-center transition 
                shadow-[0_0_4px_var(--theme-shadow)] hover:shadow-[0_0_8px_var(--theme-shadow)] border border-theme-table-heading hover:border-theme-table-heading-contrast
                ${isFullScreen ? "h-[120px] md:h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "h-[120px] md:h-[150px]"}
                ${plant.availability >= 0.7 ? "bg-theme-success" : plant.availability >= 0.65 ? "bg-theme-warning" : "bg-theme-danger"}
                `}
              >
                <Typography variant="h6" className={`!font-bold text-white ${isFullScreen ? "md:!text-3xl md:!mb-2" : ""}`}>
                  {plant.plantType}
                </Typography>
                <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
                  Vehicles: {plant.vehicles}
                </Typography>
                <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
                  UoA: {(plant.uofA * 100).toFixed(2)}%
                </Typography>
                <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
                  Availability: {(plant.availability * 100).toFixed(2)}%
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </Paper>
  );
});